import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Factory, 
  CheckCircle2, 
  AlertTriangle,
  Phone,
  ArrowLeft,
  Cog,
  Flame,
  Zap,
  Wind,
  Droplets,
  Radiation,
  AlertCircle,
  Users,
  GraduationCap,
  BookOpen
} from "lucide-react";

const industryRisks = [
  {
    icon: Cog,
    title: "סיכוני מכונות",
    description: "מכונות כבדות, חלקים נעים, נקודות לכידה ומיעוך",
    regulations: [
      "תקנות הבטיחות בעבודה (מכונות)",
      "בדיקות תקופתיות",
      "מגנים ומחסומי בטיחות"
    ]
  },
  {
    icon: Flame,
    title: "סיכוני אש ופיצוץ",
    description: "חומרים דליקים, תהליכים תרמיים, אבק תעשייתי",
    regulations: [
      "תקנות כיבוי אש",
      "ATEX לאזורים מסוכנים",
      "תוכנית כיבוי ופינוי"
    ]
  },
  {
    icon: Droplets,
    title: "חומרים מסוכנים",
    description: "כימיקלים, ממסים, חומצות ובסיסים",
    regulations: [
      "תקנות חומרים מסוכנים",
      "גיליונות בטיחות (SDS)",
      "ציוד מגן ייעודי"
    ]
  },
  {
    icon: Zap,
    title: "סיכוני חשמל",
    description: "מתח גבוה, לוחות חשמל, עבודה בסביבה רטובה",
    regulations: [
      "תקנות החשמל",
      "בדיקות תקופתיות",
      "נעילה ותיוג (LOTO)"
    ]
  },
  {
    icon: Wind,
    title: "איכות אוויר וחשיפות",
    description: "אבק, עשן, אדים, רעש מופרז",
    regulations: [
      "ערכי סף חשיפה (TLV)",
      "ניטור סביבתי",
      "אוורור והנדסה"
    ]
  },
  {
    icon: Radiation,
    title: "קרינה ופליטות",
    description: "קרינה מייננת ולא מייננת, פליטות סביבתיות",
    regulations: [
      "תקנות הקרינה",
      "ניטור חשיפות",
      "רישוי והיתרים"
    ]
  }
];

const specializations = [
  {
    title: "מפעלי מזון",
    focus: "היגיינה, בטיחות מכונות, טמפרטורות קיצוניות",
    certifications: ["HACCP", "ISO 22000", "תקני GMP"]
  },
  {
    title: "תעשייה כימית",
    focus: "חומרים מסוכנים, תהליכים כימיים, סביבה",
    certifications: ["ISO 14001", "אישורי משרד הסביבה"]
  },
  {
    title: "מתכת ומכניקה",
    focus: "מכונות כבדות, ריתוך, עבודה בחום",
    certifications: ["תקני ריתוך", "בדיקות מתכת"]
  },
  {
    title: "אלקטרוניקה",
    focus: "ESD, חדרים נקיים, כימיקלים",
    certifications: ["ISO 9001", "תקני חדר נקי"]
  }
];

/**
 * דרישות מינוי ממונה בטיחות בתעשייה
 * מבוסס על תקנות ממונים על הבטיחות התשנ"ו-1996
 */
const appointmentRequirements = [
  {
    icon: Users,
    title: "סף מינוי - 50 עובדים",
    description: "מפעל תעשייתי עם 50 עובדים ומעלה חייב במינוי ממונה בטיחות (תקנה 4)"
  },
  {
    icon: AlertCircle,
    title: "ענפים מסוכנים - ללא סף",
    description: "בענפים מהתוספת השנייה (כימיה, מתכות, מכרות) - מפקח עבודה רשאי לחייב מינוי גם מתחת ל-50 עובדים"
  },
  {
    icon: GraduationCap,
    title: "ניסיון בענף",
    description: "נדרש ניסיון של 3 שנים לפחות בענף התעשייה הרלוונטי (תקנה 5)"
  },
  {
    icon: BookOpen,
    title: "השתלמות ענפית",
    description: "בענפים מהתוספת השלישית (כימיה, מזון, מתכת) נדרשת השתלמות ענפית ספציפית"
  }
];

const ourApproach = [
  {
    step: "01",
    title: "מיפוי סיכונים ייעודי",
    description: "סקר מקיף של כל תהליכי הייצור, זיהוי סיכונים ייחודיים לענף"
  },
  {
    step: "02",
    title: "התאמת נהלים",
    description: "כתיבת נהלי בטיחות מותאמים לתהליכים הספציפיים במפעל"
  },
  {
    step: "03",
    title: "הדרכה מקצועית",
    description: "הדרכות מותאמות לסיכונים הייחודיים ולתפקידים השונים"
  },
  {
    step: "04",
    title: "ניטור ושיפור",
    description: "מערכת ניטור ומעקב עם שיפור מתמיד של ביצועי הבטיחות"
  }
];

const faqs = [
  {
    question: "מתי מפעל תעשייתי חייב בממונה בטיחות?",
    answer: "לפי תקנה 4 לתקנות ממונים על הבטיחות התשנ\"ו-1996, מפעל עם 50 עובדים ומעלה חייב במינוי ממונה בטיחות. בנוסף, מפעלים בענפים מסוכנים (מהתוספת השנייה) כמו כימיה, מתכות או מכרות - מפקח עבודה אזורי רשאי לחייב מינוי גם עם פחות עובדים."
  },
  {
    question: "האם ממונה בטיחות בתעשייה צריך הסמכה מיוחדת?",
    answer: "ממונה בטיחות בתעשייה צריך תעודת ממונה בטיחות ממשרד העבודה וניסיון של 3 שנים לפחות בענף הרלוונטי. בענפים מסוימים מהתוספת השלישית (כמו כימיה ומזון) נדרשת גם השתלמות ענפית ספציפית."
  },
  {
    question: "מהי חובת ההשתלמות השנתית?",
    answer: "לפי תקנה 6, ממונה בטיחות חייב ב-4 ימי השתלמות בשנה לעדכון ידע מקצועי. בענפים מהתוספת השלישית נדרשת גם השתלמות ענפית המותאמת לסיכונים הספציפיים של הענף."
  },
  {
    question: "כמה פעמים צריך ממונה בטיחות להגיע למפעל?",
    answer: "התדירות תלויה בגודל המפעל ורמת הסיכון. המינימום הוא 32 שעות חודשיות (כיום בשבוע). מפעל גדול או מסוכן ידרוש נוכחות יומית או אף משרה מלאה ומעלה."
  }
];

export default function IndustrySafetyOfficer() {
  return (
    <Layout>
      <Helmet>
        <title>ממונה בטיחות בתעשייה | Adam Safety</title>
        <meta name="description" content="שירותי ממונה בטיחות מתמחים בתעשייה - מפעלים, ייצור וענפים תעשייתיים. מומחיות בסיכונים ייחודיים ורגולציה ספציפית לפי תקנות התשנו." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Factory className="w-5 h-5" />
              <span className="text-sm font-medium">התמחות בתעשייה</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              ממונה בטיחות בתעשייה
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              מומחיות בסיכונים ייחודיים לסביבת הייצור - 
              מכונות, חומרים מסוכנים, תהליכים תעשייתיים ורגולציה ספציפית
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  ייעוץ למפעלים
                </Button>
              </Link>
              <Link to="/services/construction-safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  ממונה באתרי בנייה
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointment Requirements */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              דרישות מינוי ממונה בטיחות בתעשייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              לפי תקנות ממונים על הבטיחות התשנ"ו-1996
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            {appointmentRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="icon-container">
                    <req.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{req.title}</h3>
                    <p className="text-muted-foreground text-sm">{req.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>התוספת השנייה (ענפים מסוכנים):</strong> בנייה, כרייה וחציבה, ייצור כימיקלים, ייצור מתכות בסיסיות, מחצבות ומכרות.
                  במפעלים בענפים אלה מפקח עבודה רשאי לחייב מינוי ממונה גם עם פחות מ-50 עובדים.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Risks */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סיכונים ייחודיים בתעשייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              הבנה מעמיקה של הסיכונים התעשייתיים והרגולציה הרלוונטית
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {industryRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="icon-container mb-4">
                  <risk.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{risk.title}</h3>
                <p className="text-muted-foreground mb-4">{risk.description}</p>
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-accent font-medium mb-2">רגולציה רלוונטית:</p>
                  <ul className="space-y-1">
                    {risk.regulations.map((reg, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-accent" />
                        {reg}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              התמחויות ענפיות
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              ניסיון וידע מעמיקים בענפי תעשייה שונים
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {specializations.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <h3 className="text-lg font-bold mb-2">{spec.title}</h3>
                <p className="text-primary-foreground/80 text-sm mb-4">{spec.focus}</p>
                <div className="flex flex-wrap gap-2">
                  {spec.certifications.map((cert, i) => (
                    <span 
                      key={i} 
                      className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              הגישה שלנו לתעשייה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              תהליך מובנה להתאמת מערך בטיחות לצרכים התעשייתיים
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {ourApproach.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <div className="text-4xl font-bold text-accent/20 mb-3">{step.step}</div>
                <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              מפעל שלכם זקוק לממונה בטיחות מומחה?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              צוות המומחים שלנו מתמחה בבטיחות תעשייתית ומכיר את האתגרים הייחודיים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  יצירת קשר
                </Button>
              </Link>
              <Link to="/industries">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  ענפים נוספים
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
